// Wave 20.49cp — Share-card dialog (web). Parity with
// mobile/src/components/ShareCardSheet.tsx.
//
// Draws the 1080×1920 card on a canvas (previewed scaled-down via
// CSS — the canvas itself stays full-res so the exported PNG is
// exactly what's shown), then:
//   • "Share…" when the browser can share files (mobile Safari /
//     Chrome) — lands in the OS sheet with Instagram, Facebook, etc.
//   • otherwise "Download image" + "Copy caption".
// Rider-safe copy on every failure path.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { colors } from '../theme';
import { useUnitSystem } from '../services/useUnitSystem';
import {
  CARD_H,
  CARD_W,
  buildShareCardModel,
  canWebShareFiles,
  canvasToPng,
  drawShareCard,
  downloadBlob,
  shareCardFile,
  type PeakLike,
  type ShareableActivity,
} from '../services/shareCard';

interface Props {
  open: boolean;
  onClose: () => void;
  activity: ShareableActivity;
  /** Wave 20.49cr — the session's peaks; numbered pins + legend. */
  intervals?: PeakLike[];
}

export default function ShareCardDialog({ open, onClose, activity, intervals }: Props) {
  const units = useUnitSystem();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [rendering, setRendering] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const model = useMemo(
    () => buildShareCardModel(activity, units, intervals ?? []),
    [activity, units, intervals],
  );
  const canShare = useMemo(() => canWebShareFiles(), []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRendering(true);
    setNote(null);
    // Let the canvas mount before drawing.
    const id = window.requestAnimationFrame(async () => {
      const c = canvasRef.current;
      if (!c) return;
      try {
        await drawShareCard(c, model);
      } finally {
        if (!cancelled) setRendering(false);
      }
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(id);
    };
  }, [open, model]);

  const getPng = useCallback(async () => {
    const c = canvasRef.current;
    if (!c) throw new Error('no canvas');
    return canvasToPng(c);
  }, []);

  const share = useCallback(async () => {
    setBusy(true);
    setNote(null);
    try {
      const blob = await getPng();
      await shareCardFile(blob, model.caption);
    } catch (e: any) {
      // AbortError = the rider dismissed the sheet; stay quiet.
      if (e?.name !== 'AbortError') setNote('Couldn’t open the share sheet — try Download instead.');
    } finally {
      setBusy(false);
    }
  }, [getPng, model.caption]);

  const download = useCallback(async () => {
    setBusy(true);
    setNote(null);
    try {
      downloadBlob(await getPng());
      setNote('Saved — post it to Instagram or Facebook from your phone.');
    } catch {
      setNote('Couldn’t export the image — try again.');
    } finally {
      setBusy(false);
    }
  }, [getPng]);

  const copyCaption = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(model.caption);
      setNote('Caption copied.');
    } catch {
      setNote('Couldn’t copy — select the caption text below.');
    }
  }, [model.caption]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { backgroundColor: colors.surface } }}>
      <DialogTitle sx={{ color: colors.text, fontWeight: 800 }}>Share your card</DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', width: '100%', maxWidth: 300, mx: 'auto', aspectRatio: `${CARD_W} / ${CARD_H}` }}>
          <canvas
            ref={canvasRef}
            width={CARD_W}
            height={CARD_H}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              borderRadius: 12,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bg,
            }}
          />
          {rendering && (
            <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
              <CircularProgress size={22} sx={{ color: colors.brand }} />
            </Stack>
          )}
        </Box>
        <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem', mt: 1.5, textAlign: 'center' }}>
          {model.caption}
        </Typography>
        {note && (
          <Typography sx={{ color: colors.text, fontSize: '0.8rem', mt: 1, textAlign: 'center' }}>
            {note}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={copyCaption} startIcon={<ContentCopyIcon />} sx={{ color: colors.textMuted }}>
          Copy caption
        </Button>
        <Box sx={{ flex: 1 }} />
        {canShare ? (
          <Button
            onClick={share}
            disabled={rendering || busy}
            variant="contained"
            startIcon={<IosShareIcon />}
            sx={{ backgroundColor: colors.brand, color: colors.bg, fontWeight: 800, '&:hover': { backgroundColor: colors.brandBright } }}
          >
            Share…
          </Button>
        ) : (
          <Button
            onClick={download}
            disabled={rendering || busy}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ backgroundColor: colors.brand, color: colors.bg, fontWeight: 800, '&:hover': { backgroundColor: colors.brandBright } }}
          >
            Download image
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
